import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, MapPin, TrendingUp } from "lucide-react";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal } from "@/components/shared/Reveal";
import { US_STATES } from "@/components/auth/signupConstants";
import { StateHeatmap } from "@/components/sections/StateHeatmap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WhereWereLaunching = () => {
  const [zip, setZip] = useState("");
  const [state, setState] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [topStates, setTopStates] = useState([]);

  const loadTopStates = () => {
    axios
      .get(`${API}/leads/states`)
      .then((res) => setTopStates(res.data?.states || []))
      .catch(() => setTopStates([]));
  };

  useEffect(() => {
    loadTopStates();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        email,
        location: [zip, state].filter(Boolean).join(", ") || null,
        role: "Geo Notify",
        segment: "geo_notify",
        zip_code: zip || null,
        state: state || null,
      });
      setDone(true);
      loadTopStates();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="launching" data-testid="where-launching-section" className="py-24 md:py-32 bg-rr-surface">
      <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
        <SectionHeading
          eyebrow="Building the RelayRigs Network"
          title="Where RelayRigs Is Launching"
          description="Launching in the United States."
          align="center"
          testId="where-launching"
        />

        <Reveal delay={0.15}>
          <div className="mt-8 max-w-2xl mx-auto bg-white rounded-[2rem] border border-rr-line p-6 md:p-8 shadow-sm">
            <StateHeatmap />
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          {done ? (
            <div data-testid="where-launching-success" className="mt-6 bg-white rounded-2xl border border-rr-line p-6 max-w-md mx-auto">
              <p className="font-bold text-rr-navy">You're on the list!</p>
              <p className="text-sm text-rr-muted mt-1">We'll notify you when RelayRigs is available in your area.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="where-launching-form" className="mt-6 max-w-lg mx-auto flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                data-testid="where-launching-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 border border-rr-line rounded-full px-5 py-3.5 text-sm focus:outline-none focus:border-rr-orange"
              />
              <input
                data-testid="where-launching-zip-input"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="ZIP code (optional)"
                className="flex-1 border border-rr-line rounded-full px-5 py-3.5 text-sm focus:outline-none focus:border-rr-orange"
              />
              <select
                required
                data-testid="where-launching-state-select"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="flex-1 border border-rr-line rounded-full px-5 py-3.5 text-sm bg-white focus:outline-none focus:border-rr-orange"
              >
                <option value="">Your state</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button
                type="submit"
                disabled={submitting}
                data-testid="where-launching-submit-button"
                className="bg-rr-orange text-white font-bold px-6 py-3.5 rounded-full hover:bg-rr-navy transition-colors flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Notify Me <ArrowRight size={15} /></>}
              </button>
            </form>
          )}
        </Reveal>
        <p className="text-xs text-rr-muted mt-5 flex items-center justify-center gap-1.5">
          <MapPin size={12} /> Helps us prioritize where RelayRigs launches first.
        </p>

        {topStates.length > 0 && (
          <Reveal delay={0.3}>
            <div data-testid="where-launching-state-ranking" className="mt-12 max-w-md mx-auto bg-white rounded-2xl border border-rr-line p-6 text-left">
              <div className="flex items-center gap-2 text-rr-navy font-bold text-sm mb-4">
                <TrendingUp size={16} className="text-rr-orange" /> Top States by Launch Interest
              </div>
              <ol className="space-y-2.5">
                {topStates.map((s, idx) => (
                  <li key={s.state} data-testid={`where-launching-state-rank-${idx}`} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-rr-navy font-semibold">
                      <span className="text-rr-orange font-bold w-4">{idx + 1}</span> {s.state}
                    </span>
                    <span className="text-rr-muted">{s.count} sign-up{s.count === 1 ? "" : "s"}</span>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
